import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetTrigger,
} from '@/components/ui/sheet';
import {
    Home,
    Puzzle,
    Key,
    User,
    CreditCard,
    Menu,
    Plus,
    LogOut,
    Copy,
    Check,
    Search,
    ExternalLink,
    Sparkles,
    ChevronRight,
    Trash2,
    Zap,
    Lock
} from 'lucide-react';
import pluckrLogo from '../assets/images/pluckr-logo.png';
import McpConnectionModal from '../components/dashboard/McpConnectionModal';
import { processPayment } from '@/services/paymentService';
import ThemeToggle from '@/components/ThemeToggle';
import { useThemeMode } from '@/hooks/useThemeMode';

// Landing-style MCP cards for the Dashboard (only 2 unlocked for now)
const dashboardMcpCards = [
    {
        id: 'razorpay-mcp',
        title: 'Razorpay MCP',
        description: "Accept payments with India's #1 payment gateway. Verified flows for orders, payments, refunds, and webhooks.",
        tags: ['Payments', 'Verified'],
        unlocked: false,
    },
    {
        id: 'frontend-mcp',
        title: 'Frontend MCP',
        description: 'Pre-built MCP servers for UI components and frontend scaffolding. Works with React + modern tooling.',
        tags: ['Frontend', 'UI'],
        unlocked: false,
    },
    {
        id: 'supabase-auth-mcp',
        title: 'Supabase Auth MCP',
        description: 'Authentication flows with magic links, OTP, sessions, and RLS-friendly patterns.',
        tags: ['Auth', 'Database'],
        unlocked: false,
    },
    {
        id: 'shadcn-ui-mcp',
        title: 'Shadcn UI MCP',
        description: 'Production-ready UI components and patterns. Generate consistent, accessible components fast.',
        tags: ['Design', 'Components'],
        unlocked: false,
    },
    {
        id: 'stripe-mcp',
        title: 'Stripe MCP',
        description: 'Payments, subscriptions, and invoices with battle-tested patterns and webhooks.',
        tags: ['Payments'],
        unlocked: false,
    },
    {
        id: 'resend-mcp',
        title: 'Resend Email MCP',
        description: 'Transactional email flows with templates, retries, and deliverability best practices.',
        tags: ['Email'],
        unlocked: false,
    },
    {
        id: 'twilio-mcp',
        title: 'Twilio SMS/OTP MCP',
        description: 'SMS notifications and OTP flows with rate-limiting and safe retry semantics.',
        tags: ['SMS', 'OTP'],
        unlocked: false,
    },
    {
        id: 'postgres-mcp',
        title: 'Postgres MCP',
        description: 'Schema + query helpers with migrations and safe query building patterns.',
        tags: ['Database'],
        unlocked: false,
    },
];

function DashboardMcpCard({ card, onPluckNow, onQuickInstall }) {
    const { title, description, tags, unlocked } = card;
    const isPurchasable = card.id === 'razorpay-mcp';

    return (
        <div
            className="bg-white dark:bg-[#282929] rounded-xl md:rounded-2xl border border-[#e0e0e0] dark:border-white/15 overflow-hidden flex flex-col h-full transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl"
            style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)', transformStyle: 'preserve-3d' }}
        >
            {/* Header strip */}
            <div className="px-4 py-3 md:px-5 md:py-4 bg-[rgba(124,182,135,0.12)] dark:bg-[rgba(155,227,143,0.10)]">
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-xs font-semibold text-[#4a8a54] tracking-wide uppercase truncate">
                            MCP
                        </p>
                        <p className="text-sm md:text-base font-semibold text-[#1a1a1a] dark:text-white truncate">{title}</p>
                    </div>
                    {!unlocked && !isPurchasable && (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/70 dark:bg-[#161616]/70 border border-[#7cb687]/25 dark:border-white/20">
                            <Lock className="w-3.5 h-3.5 text-[#4a8a54]" />
                            <span className="text-[11px] font-semibold text-[#4a8a54]">Coming soon</span>
                        </div>
                    )}
                    {!unlocked && isPurchasable && (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/70 dark:bg-[#161616]/70 border border-[#7cb687]/25 dark:border-white/20">
                            <Lock className="w-3.5 h-3.5 text-[#4a8a54]" />
                            <span className="text-[11px] font-semibold text-[#4a8a54]">Locked</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Body */}
            <div className="p-5 md:p-6 flex flex-col flex-grow">
                <p className="text-sm text-[#666666] dark:text-[#e0e0e0] leading-relaxed mb-4 flex-grow line-clamp-3">
                    {description}
                </p>

                {tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {tags.map((tag) => (
                            <span
                                key={tag}
                                className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#7cb687]/15 dark:bg-[rgba(155,227,143,0.12)] text-[#4a8a54] dark:text-[#9be38f]"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>
                )}

                <button
                    type="button"
                    disabled={!unlocked && !isPurchasable}
                    onClick={() => {
                        if (unlocked) {
                            onQuickInstall?.(card);
                            return;
                        }
                        if (isPurchasable && !unlocked) {
                            onPluckNow?.();
                            return;
                        }
                    }}
                    className={`w-full py-3 px-6 rounded-lg font-semibold text-sm transition-all ${
                        isPurchasable && !unlocked
                            ? 'text-white cursor-pointer'
                            : !unlocked
                            ? 'text-white disabled:opacity-60 disabled:cursor-not-allowed'
                            : 'text-white'
                    }`}
                    style={{ background: '#7cb687' }}
                >
                    {unlocked ? 'Quick Install' : isPurchasable ? 'Pluck Now' : 'Coming soon'}
                </button>
            </div>
        </div>
    );
}

const navItems = [
    { icon: Home, label: 'Home', path: '/dashboard' },
    { icon: Key, label: 'API Keys', path: '/dashboard/api-keys' },
    { icon: CreditCard, label: 'Plan & Billing', path: '/dashboard/plan-billing' },
    { icon: User, label: 'Profile', path: '/dashboard/profile' },
];

function ApiKeysPanel({ apiKeys, onDelete, onCreate, isLoading, newlyCreatedKey, onDismissNewKey }) {
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [copiedKey, setCopiedKey] = useState(false);

    const canCreate = newKeyName.trim().length > 0;

    const handleCreate = () => {
        if (!canCreate) return;
        onCreate?.(newKeyName.trim());
        setNewKeyName('');
        setIsCreateOpen(false);
    };

    const handleCopyNewKey = () => {
        if (newlyCreatedKey) {
            navigator.clipboard.writeText(newlyCreatedKey);
            setCopiedKey(true);
            setTimeout(() => setCopiedKey(false), 2000);
        }
    };

    return (
        <div className="max-w-[1000px] mx-auto p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <h1
                        className="text-2xl font-bold text-[#1a1a1a] dark:text-white"
                        style={{ fontFamily: "'Lora', serif" }}
                    >
                        API Keys
                    </h1>
                    <p className="text-[#5a5a5a] dark:text-[#e0e0e0] text-sm mt-2">
                        As an owner of this project, you can view and manage all API keys in this project.
                    </p>
                    <p className="text-[#9a9a9a] dark:text-white/60 text-sm mt-2 max-w-[820px]">
                        Do not share your API key with others or expose it in the browser or other client-side code. To protect your
                        account&apos;s security, Pluckr may automatically disable any API key that has leaked publicly.
                    </p>
                </div>

                <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                    <DialogTrigger asChild>
                        <button
                            type="button"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2d8a2d] text-white font-medium text-sm hover:opacity-95 transition-opacity whitespace-nowrap"
                        >
                            <Plus className="w-4 h-4" />
                            New API Key
                        </button>
                    </DialogTrigger>

                    <DialogContent className="max-w-xl bg-white dark:bg-[#282929] border border-[#e5e7eb] dark:border-white/15 rounded-xl p-6 [&>button]:hidden">
                        <DialogHeader className="text-left">
                            <DialogTitle className="text-[#1a1a1a] dark:text-white">Create a New API Key</DialogTitle>
                            <DialogDescription className="text-[#5a5a5a] dark:text-[#e0e0e0]">
                                Give your key a name to help you distinguish it from other keys.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-[#1a1a1a] dark:text-white">Name</label>
                            <input
                                value={newKeyName}
                                onChange={(e) => setNewKeyName(e.target.value)}
                                placeholder="Enter a name"
                                className="w-full px-3 py-2 rounded-lg border border-[#e5e7eb] dark:border-white/20 dark:bg-[#282929] dark:text-white dark:placeholder:text-white/40 text-sm outline-none focus:border-[#2d8a2d]"
                                autoFocus
                            />
                        </div>

                        <DialogFooter className="sm:justify-end">
                            <DialogClose asChild>
                                <button
                                    type="button"
                                    className="px-4 py-2 rounded-lg bg-[#f5f5f5] dark:bg-white/10 text-[#1a1a1a] dark:text-white font-medium text-sm hover:opacity-90 dark:hover:bg-white/15 transition-colors"
                                >
                                    Cancel
                                </button>
                            </DialogClose>
                            <button
                                type="button"
                                onClick={handleCreate}
                                disabled={!canCreate}
                                className="px-4 py-2 rounded-lg bg-[#2d8a2d] text-white font-medium text-sm hover:opacity-95 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Create
                            </button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Newly created key banner — shown once */}
            {newlyCreatedKey && (
                <div className="mt-4 p-4 bg-[#f8fdf7] dark:bg-[rgba(155,227,143,0.12)] border border-[#9be38f] rounded-xl">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#1a1a1a] dark:text-white mb-1">
                                Your new API key has been created
                            </p>
                            <p className="text-xs text-[#5a5a5a] dark:text-[#e0e0e0] mb-2">
                                Copy this key now — you won&apos;t be able to see it again.
                            </p>
                            <code className="block bg-white dark:bg-[#161616] border border-[#e5e7eb] dark:border-white/15 rounded-lg px-3 py-2 text-xs text-[#1a1a1a] dark:text-white font-mono break-all select-all">
                                {newlyCreatedKey}
                            </code>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                            <button
                                type="button"
                                onClick={handleCopyNewKey}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#2d8a2d] text-white text-xs font-medium hover:opacity-95 transition-opacity"
                            >
                                {copiedKey ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                {copiedKey ? 'Copied!' : 'Copy'}
                            </button>
                            <button
                                type="button"
                                onClick={onDismissNewKey}
                                className="px-3 py-1.5 rounded-md bg-[#f5f5f5] dark:bg-white/10 text-[#5a5a5a] dark:text-white/70 text-xs font-medium hover:opacity-90 dark:hover:bg-white/15 transition-colors"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white dark:bg-[#282929] rounded-xl border border-[#e5e7eb] dark:border-white/15 overflow-hidden mt-6">
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="w-6 h-6 border-2 border-[#9be38f] border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-[#fafafa] dark:bg-[#161616]">
                                <th className="text-left px-4 sm:px-6 py-4 text-xs font-medium text-[#9a9a9a] dark:text-white/60">Name</th>
                                <th className="text-left px-4 sm:px-6 py-4 text-xs font-medium text-[#9a9a9a] dark:text-white/60">API Key</th>
                                <th className="text-left px-4 sm:px-6 py-4 text-xs font-medium text-[#9a9a9a] dark:text-white/60">Created At</th>
                                <th className="w-12" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#f0f0f0] dark:divide-white/10">
                            {apiKeys.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center text-sm text-[#9a9a9a] dark:text-white/60">
                                        No API keys yet. Click &quot;+ New API Key&quot; to create one.
                                    </td>
                                </tr>
                            ) : apiKeys.map((key) => (
                                <tr key={key.id} className="hover:bg-[#fafafa] dark:hover:bg-white/5 transition-colors">
                                    <td className="px-4 sm:px-6 py-4 text-sm text-[#1a1a1a] dark:text-white font-medium">{key.name}</td>
                                    <td className="px-4 sm:px-6 py-4 text-sm text-[#5a5a5a] dark:text-[#e0e0e0] font-mono">{key.apiKey}</td>
                                    <td className="px-4 sm:px-6 py-4 text-sm text-[#5a5a5a] dark:text-[#e0e0e0]">{key.createdAt}</td>
                                    <td className="px-4 py-4 text-right">
                                        <button
                                            type="button"
                                            onClick={() => onDelete(key.id)}
                                            className="p-2 rounded-md text-red-500 hover:bg-[#f5f5f5] dark:hover:bg-white/10 transition-colors"
                                            title="Delete"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                )}
            </div>
        </div>
    );
}

function ProfilePanel({ user, onUserUpdate }) {
    const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.pluckr.ai');
    
    const [firstName, setFirstName] = useState(user?.firstName || '');
    const [lastName, setLastName] = useState(user?.lastName || '');
    const [company, setCompany] = useState(user?.company || '');
    const [isSavingProfile, setIsSavingProfile] = useState(false);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setIsSavingProfile(true);
        
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_URL}/api/auth/profile`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ firstName, lastName, company }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to update profile');
            }

            // Update localStorage with new user data
            localStorage.setItem('user', JSON.stringify(data.user));
            
            // Notify parent component of user update
            if (onUserUpdate) {
                onUserUpdate(data.user);
            }
            
            toast.success('Profile saved successfully');
        } catch (error) {
            toast.error(error.message || 'Failed to save profile');
        } finally {
            setIsSavingProfile(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        
        if (!currentPassword || !newPassword) {
            toast.error('Please fill in all password fields');
            return;
        }
        if (newPassword.length < 6) {
            toast.error('New password must be at least 6 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }
        
        setIsChangingPassword(true);
        
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_URL}/api/auth/change-password`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ currentPassword, newPassword }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to change password');
            }

            toast.success('Password updated successfully');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            toast.error(error.message || 'Failed to change password');
        } finally {
            setIsChangingPassword(false);
        }
    };

    return (
        <div className="max-w-[1000px] mx-auto p-4 sm:p-6">
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <h1
                        className="text-2xl font-bold text-[#1a1a1a] dark:text-white"
                        style={{ fontFamily: "'Lora', serif" }}
                    >
                        Profile
                    </h1>
                    <p className="text-[#5a5a5a] dark:text-[#e0e0e0] text-sm mt-2">
                        Manage your personal details and account security.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 mt-6">
                <form onSubmit={handleSaveProfile} className="bg-white dark:bg-[#282929] rounded-xl border border-[#e5e7eb] dark:border-white/15 p-6">
                    <h2 className="text-base font-semibold text-[#1a1a1a] dark:text-white">Personal info</h2>
                    <p className="text-xs text-[#9a9a9a] dark:text-white/60 mt-1">This info is used to personalize your dashboard.</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                        <div>
                            <label className="block text-xs font-medium text-[#5a5a5a] dark:text-white/70">First name</label>
                            <input
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                className="mt-1.5 w-full px-3 py-2 rounded-lg border border-[#e5e7eb] dark:border-white/20 dark:bg-[#282929] dark:text-white dark:placeholder:text-white/40 text-sm outline-none focus:border-[#9be38f]"
                                placeholder="Enter first name"
                                autoComplete="given-name"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-[#5a5a5a] dark:text-white/70">Last name</label>
                            <input
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                className="mt-1.5 w-full px-3 py-2 rounded-lg border border-[#e5e7eb] dark:border-white/20 dark:bg-[#282929] dark:text-white dark:placeholder:text-white/40 text-sm outline-none focus:border-[#9be38f]"
                                placeholder="Enter last name"
                                autoComplete="family-name"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-[#5a5a5a] dark:text-white/70">Company</label>
                            <input
                                value={company}
                                onChange={(e) => setCompany(e.target.value)}
                                className="mt-1.5 w-full px-3 py-2 rounded-lg border border-[#e5e7eb] dark:border-white/20 dark:bg-[#282929] dark:text-white dark:placeholder:text-white/40 text-sm outline-none focus:border-[#9be38f]"
                                placeholder="Optional"
                                autoComplete="organization"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-[#5a5a5a] dark:text-white/70">Email</label>
                            <input
                                value={user?.email || ''}
                                disabled
                                className="mt-1.5 w-full px-3 py-2 rounded-lg border border-[#e5e7eb] dark:border-white/15 bg-[#fafafa] dark:bg-[#161616] text-sm text-[#9a9a9a] dark:text-white/50 cursor-not-allowed"
                            />
                        </div>
                    </div>

                    <div className="mt-6 flex items-center justify-end">
                        <button
                            type="submit"
                            disabled={isSavingProfile}
                            className="px-4 py-2 rounded-lg bg-[#2d8a2d] text-white font-medium text-sm hover:opacity-95 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSavingProfile ? 'Saving...' : 'Save changes'}
                        </button>
                    </div>
                </form>

                <form onSubmit={handleChangePassword} className="bg-white dark:bg-[#282929] rounded-xl border border-[#e5e7eb] dark:border-white/15 p-6">
                    <h2 className="text-base font-semibold text-[#1a1a1a] dark:text-white">Password</h2>
                    <p className="text-xs text-[#9a9a9a] dark:text-white/60 mt-1">Update your password to keep your account secure.</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                        <div className="sm:col-span-2">
                            <label className="block text-xs font-medium text-[#5a5a5a] dark:text-white/70">Current password</label>
                            <input
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                type="password"
                                className="mt-1.5 w-full px-3 py-2 rounded-lg border border-[#e5e7eb] dark:border-white/20 dark:bg-[#282929] dark:text-white dark:placeholder:text-white/40 text-sm outline-none focus:border-[#9be38f]"
                                placeholder="••••••••"
                                autoComplete="current-password"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-[#5a5a5a] dark:text-white/70">New password</label>
                            <input
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                type="password"
                                className="mt-1.5 w-full px-3 py-2 rounded-lg border border-[#e5e7eb] dark:border-white/20 dark:bg-[#282929] dark:text-white dark:placeholder:text-white/40 text-sm outline-none focus:border-[#9be38f]"
                                placeholder="••••••••"
                                autoComplete="new-password"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-[#5a5a5a] dark:text-white/70">Confirm new password</label>
                            <input
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                type="password"
                                className="mt-1.5 w-full px-3 py-2 rounded-lg border border-[#e5e7eb] dark:border-white/20 dark:bg-[#282929] dark:text-white dark:placeholder:text-white/40 text-sm outline-none focus:border-[#9be38f]"
                                placeholder="••••••••"
                                autoComplete="new-password"
                            />
                        </div>
                    </div>

                    <div className="mt-6 flex items-center justify-end">
                        <button
                            type="submit"
                            disabled={isChangingPassword}
                            className="px-4 py-2 rounded-lg bg-[#9be38f] text-[#1a1a1a] font-medium text-sm hover:bg-[#8ad37f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isChangingPassword ? 'Updating...' : 'Update password'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function PlanBillingPanel({ userPlan = 'free' }) {
    const [loadingTier, setLoadingTier] = useState(null);

    const handlePayment = async (tier) => {
        setLoadingTier(tier);
        try {
            const result = await processPayment(tier);
            if (result.success) {
                toast.success('Payment successful! Your plan has been upgraded.');
                // Reload page to reflect new plan status
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            }
        } catch (error) {
            toast.error(error.message || 'Payment failed');
        } finally {
            setLoadingTier(null);
        }
    };

    const isStarterPlan = userPlan === 'starter';
    const isProPlan = userPlan === 'pro';

    return (
        <div className="max-w-[1100px] mx-auto p-4 sm:p-6">
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <h1
                        className="text-2xl font-bold text-[#1a1a1a] dark:text-white"
                        style={{ fontFamily: "'Lora', serif" }}
                    >
                        Plan & Billing
                    </h1>
                    <p className="text-[#5a5a5a] dark:text-[#e0e0e0] text-sm mt-2">
                        Choose a plan that fits your workflow.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                {/* Starter */}
                <div className="bg-white dark:bg-[#282929] rounded-2xl border border-[#e5e7eb] dark:border-white/15 p-6 sm:p-8">
                    <h2 className="text-lg font-semibold text-[#1a1a1a] dark:text-white">Starter</h2>
                    <div className="mt-6 flex items-end gap-2">
                        <div className="text-5xl sm:text-6xl font-extrabold text-[#1a1a1a] dark:text-white">$4</div>
                    </div>
                    <p className="text-[#5a5a5a] dark:text-[#e0e0e0] mt-3">For students, builders trying Pluckr</p>

                    <div className="mt-8 space-y-4">
                        <div className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-[#22c55e] mt-0.5" />
                            <span className="text-[#1a1a1a] dark:text-[#e0e0e0]">Access to Limited MCPs</span>
                        </div>
                        <div className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-[#22c55e] mt-0.5" />
                            <span className="text-[#1a1a1a] dark:text-[#e0e0e0]">Limited MCP calls / month</span>
                        </div>
                        <div className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-[#22c55e] mt-0.5" />
                            <span className="text-[#1a1a1a] dark:text-[#e0e0e0]">Community support</span>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => !isStarterPlan && handlePayment('starter')}
                        disabled={loadingTier === 'starter' || isStarterPlan}
                        className={`mt-10 w-full h-12 rounded-xl font-semibold transition-colors disabled:cursor-not-allowed ${
                            isStarterPlan
                                ? 'bg-[#e5e7eb] dark:bg-[#3a3b3b] text-[#666666] dark:text-[#aaa]'
                                : 'bg-[#9be38f] text-[#1a1a1a] hover:bg-[#8ad37f] disabled:opacity-50'
                        }`}
                    >
                        {isStarterPlan
                            ? 'Current Plan'
                            : loadingTier === 'starter'
                            ? 'Processing...'
                            : 'Pluck Now'}
                    </button>
                </div>

                {/* Pro */}
                <div className="bg-white dark:bg-[#282929] rounded-2xl border border-[#e5e7eb] dark:border-white/15 p-6 sm:p-8">
                    <h2 className="text-lg font-semibold text-[#1a1a1a] dark:text-white">Pro</h2>
                    <div className="mt-6 flex items-end gap-3">
                        <div className="text-5xl sm:text-6xl font-extrabold text-[#1a1a1a] dark:text-white">$15</div>
                        <div className="text-xl text-[#5a5a5a] dark:text-[#e0e0e0] mb-2">/month</div>
                    </div>
                    <p className="text-[#5a5a5a] dark:text-[#e0e0e0] mt-3">For startups, serious devs</p>

                    <div className="mt-8 space-y-4">
                        <div className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-[#22c55e] mt-0.5" />
                            <span className="text-[#1a1a1a] dark:text-[#e0e0e0]">Full access to core MCPs (Payments, Auth, Design, Infra)</span>
                        </div>
                        <div className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-[#22c55e] mt-0.5" />
                            <span className="text-[#1a1a1a] dark:text-[#e0e0e0]">Higher MCP usage limits</span>
                        </div>
                        <div className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-[#22c55e] mt-0.5" />
                            <span className="text-[#1a1a1a] dark:text-[#e0e0e0]">Early access to new MCPs</span>
                        </div>
                        <div className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-[#22c55e] mt-0.5" />
                            <span className="text-[#1a1a1a] dark:text-[#e0e0e0]">Priority updates</span>
                        </div>
                        <div className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-[#22c55e] mt-0.5" />
                            <span className="text-[#1a1a1a] dark:text-[#e0e0e0]">Email support</span>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="mt-10 w-full h-12 rounded-xl bg-[#9be38f] text-[#1a1a1a] font-semibold hover:bg-[#8ad37f] transition-colors"
                    >
                        Join waitlist
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function Dashboard() {
    const [user, setUser] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [selectedMcp, setSelectedMcp] = useState(null);
    const [apiKeys, setApiKeys] = useState([]);
    const [apiKeysLoading, setApiKeysLoading] = useState(false);
    const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { resolvedTheme } = useThemeMode();
    const isDark = resolvedTheme === 'dark';

    const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.pluckr.ai');

    const getAuthHeaders = () => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
    });

    // Fetch API keys from backend
    const fetchApiKeys = async () => {
        try {
            setApiKeysLoading(true);
            const response = await fetch(`${API_URL}/api/apikeys`, {
                headers: getAuthHeaders(),
            });
            const data = await response.json();
            if (data.success) {
                setApiKeys(data.data);
            }
        } catch (error) {
            console.error('Failed to fetch API keys:', error);
        } finally {
            setApiKeysLoading(false);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        const userData = localStorage.getItem('user');

        if (!token || !userData) {
            navigate('/early-access');
            return;
        }

        try {
            setUser(JSON.parse(userData));
        } catch (error) {
            console.error('Failed to parse user data:', error);
            navigate('/early-access');
        }
    }, [navigate]);

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        toast.success('Logged out successfully');
        setTimeout(() => {
            navigate('/');
        }, 1000);
    };

    const handleCopy = (id, url) => {
        navigator.clipboard.writeText(url);
        setCopiedId(id);
        toast.success('Plugin URL copied!');
        setTimeout(() => setCopiedId(null), 2000);
    };


    useEffect(() => {
        setMobileNavOpen(false);
    }, [location.pathname]);

    // Fetch API keys when navigating to the API keys page
    useEffect(() => {
        if (location.pathname === '/dashboard/api-keys' && user) {
            fetchApiKeys();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, user]);

    if (!user) {
        return null;
    }

    const userInitial = user.email?.charAt(0).toUpperCase() || 'U';
    const userName = user.email?.split('@')[0] || 'User';
    const isApiKeysPage = location.pathname === '/dashboard/api-keys';
    const isProfilePage = location.pathname === '/dashboard/profile';
    const isPlanBillingPage = location.pathname === '/dashboard/plan-billing';

    const unlockedMcps = Array.isArray(user.unlockedMcps) ? user.unlockedMcps : [];
    const dashboardCards = dashboardMcpCards.map((card) => ({
        ...card,
        unlocked: unlockedMcps.includes(card.id),
    }));

    const currentMobileTitle = (() => {
        if (isApiKeysPage) return 'API Keys';
        if (isProfilePage) return 'Profile';
        if (isPlanBillingPage) return 'Plan & Billing';
        if (location.pathname.startsWith('/dashboard')) return 'Dashboard';
        return 'Pluckr';
    })();

    const isNavItemActive = (itemPath) => {
        if (itemPath === '/dashboard') return location.pathname === '/dashboard';
        return location.pathname.startsWith(itemPath);
    };

    const handleDeleteApiKey = async (id) => {
        try {
            const response = await fetch(`${API_URL}/api/apikeys/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            const data = await response.json();
            if (data.success) {
                setApiKeys((prev) => prev.filter((k) => k.id !== id));
                toast.success('API key deleted');
            } else {
                toast.error(data.message || 'Failed to delete API key');
            }
        } catch (error) {
            console.error('Delete API key error:', error);
            toast.error('Failed to delete API key');
        }
    };

    const handleCreateApiKey = async (name) => {
        try {
            const response = await fetch(`${API_URL}/api/apikeys`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ name }),
            });
            const data = await response.json();
            if (data.success) {
                // Show the raw key to user (only time it's visible)
                setNewlyCreatedKey(data.data.key);
                // Add masked version to list
                setApiKeys((prev) => [{
                    id: data.data.id,
                    name: data.data.name,
                    apiKey: `${data.data.prefix}...${data.data.suffix}`,
                    createdAt: data.data.createdAt?.slice(0, 10),
                }, ...prev]);
                toast.success('API key created — copy it now!');
            } else {
                toast.error(data.message || 'Failed to create API key');
            }
        } catch (error) {
            console.error('Create API key error:', error);
            toast.error('Failed to create API key');
        }
    };

    return (
        <div className="min-h-screen flex bg-background" data-testid="dashboard-page">
            <Toaster
                position="top-right"
                theme={resolvedTheme}
                toastOptions={{
                    style: {
                        background: isDark ? '#282929' : '#ffffff',
                        color: isDark ? '#ffffff' : '#1a1a1a',
                        border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e0e0e0',
                    },
                }}
            />

            {/* Desktop-only: keep toggle pinned top-right */}
            <ThemeToggle className="hidden lg:grid fixed top-2.5 right-4 z-[60]" />

            {/* Mobile Top Bar + Drawer */}
            <div className="lg:hidden">
                <div className="fixed top-0 left-0 right-0 z-40 h-14 bg-white dark:bg-[#161616] border-b border-[#e5e7eb] dark:border-white/10 flex items-center px-4">
                    <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                        <SheetTrigger asChild>
                            <button
                                type="button"
                                className="p-2 -ml-2 rounded-md hover:bg-[#f5f5f5] dark:hover:bg-white/10 text-[#1a1a1a] dark:text-white"
                                aria-label="Open navigation"
                            >
                                <Menu className="w-5 h-5" />
                            </button>
                        </SheetTrigger>

                        <SheetContent side="left" className="bg-white dark:bg-[#161616] p-0 w-[280px]">
                            {/* Logo */}
                            <div className="p-5 border-b border-[#e5e7eb] dark:border-white/10">
                                <SheetClose asChild>
                                    <Link to="/" className="flex items-center gap-2">
                                        <img src={pluckrLogo} alt="Pluckr" className="h-7 w-auto" />
                                        <span
                                            className="text-lg font-bold text-[#1a1a1a] dark:text-white"
                                            style={{ fontFamily: "'Lora', serif" }}
                                        >
                                            Pluckr
                                        </span>
                                    </Link>
                                </SheetClose>
                            </div>

                            {/* Navigation */}
                            <nav className="p-3">
                                <div className="space-y-1">
                                    {navItems.map((item) => (
                                        (() => {
                                            const active = isNavItemActive(item.path);
                                            return (
                                                <SheetClose asChild key={item.label}>
                                                    <Link
                                                        to={item.path}
                                                        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all ${active
                                                            ? 'bg-[#e8f5e3] dark:bg-[#9be38f]/15 text-[#2d8a2d] dark:text-[#9be38f] font-medium'
                                                            : 'text-[#5a5a5a] dark:text-[#e0e0e0] hover:bg-[#f5f5f5] dark:hover:bg-white/10 hover:text-[#1a1a1a] dark:hover:text-white'
                                                            }`}
                                                    >
                                                        <item.icon className="w-4 h-4" />
                                                        <span>{item.label}</span>
                                                        {active && (
                                                            <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#22c55e]" />
                                                        )}
                                                    </Link>
                                                </SheetClose>
                                            );
                                        })()
                                    ))}
                                </div>
                            </nav>

                            {/* User */}
                            <div className="p-3 border-t border-[#e5e7eb] dark:border-white/10">
                                <div className="flex items-center gap-2">
                                    <div
                                        className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                                        style={{ backgroundColor: '#9be38f', color: '#1a1a1a' }}
                                    >
                                        {userInitial}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[#1a1a1a] dark:text-white text-xs font-medium truncate">{user.email}</p>
                                        <p className="text-[#9a9a9a] dark:text-white/60 text-[10px]">
                                            {user.isVerified ? 'Verified' : 'Pending'}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setMobileNavOpen(false);
                                            handleLogout();
                                        }}
                                        className="p-1.5 rounded-md text-[#9a9a9a] dark:text-white/60 hover:text-[#1a1a1a] dark:hover:text-white hover:bg-[#f5f5f5] dark:hover:bg-white/10 transition-colors"
                                        title="Logout"
                                    >
                                        <LogOut className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>

                    <div className="ml-2 flex items-center gap-2 min-w-0">
                        <span className="text-sm font-semibold text-[#1a1a1a] dark:text-white truncate">{currentMobileTitle}</span>
                    </div>

                    <div className="ml-auto flex items-center gap-2">
                        <ThemeToggle className="h-10 w-10" />
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: '#9be38f', color: '#1a1a1a' }}>
                            {userInitial}
                        </div>
                    </div>
                </div>
            </div>

            {/* LIGHT SIDEBAR */}
            <aside className="hidden lg:flex w-56 bg-white dark:bg-[#161616] border-r border-[#e5e7eb] dark:border-white/10 flex-col fixed h-screen">
                {/* Logo */}
                <div className="p-5 border-b border-[#e5e7eb] dark:border-white/10">
                    <Link to="/" className="flex items-center gap-2">
                        <img src={pluckrLogo} alt="Pluckr" className="h-7 w-auto" />
                        <span
                            className="text-lg font-bold text-[#1a1a1a] dark:text-white"
                            style={{ fontFamily: "'Lora', serif" }}
                        >
                            Pluckr
                        </span>
                    </Link>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-3">
                    <div className="space-y-1">
                        {navItems.map((item) => (
                            (() => {
                                const active = isNavItemActive(item.path);
                                return (
                            <Link
                                key={item.label}
                                to={item.path}
                                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all ${active
                                    ? 'bg-[#e8f5e3] dark:bg-[rgba(155,227,143,0.12)] text-[#2d8a2d] dark:text-[#9be38f] font-medium'
                                    : 'text-[#5a5a5a] dark:text-[#e0e0e0] hover:bg-[#f5f5f5] dark:hover:bg-white/10 hover:text-[#1a1a1a] dark:hover:text-white'
                                    }`}
                            >
                                <item.icon className="w-4 h-4" />
                                <span>{item.label}</span>
                                {active && (
                                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#22c55e]" />
                                )}
                            </Link>
                                );
                            })()
                        ))}
                    </div>
                </nav>

                {/* User Profile */}
                <div className="p-3 border-t border-[#e5e7eb] dark:border-white/10">
                    <div className="flex items-center gap-2">
                        <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                            style={{ backgroundColor: '#9be38f', color: '#1a1a1a' }}
                        >
                            {userInitial}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[#1a1a1a] dark:text-white text-xs font-medium truncate">{user.email}</p>
                            <p className="text-[#9a9a9a] dark:text-white/60 text-[10px]">
                                {user.isVerified ? 'Verified' : 'Pending'}
                            </p>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="p-1.5 rounded-md text-[#9a9a9a] dark:text-white/60 hover:text-[#1a1a1a] dark:hover:text-white hover:bg-[#f5f5f5] dark:hover:bg-white/10 transition-colors"
                            title="Logout"
                        >
                            <LogOut className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-h-screen lg:ml-56 pt-14 lg:pt-0">
                {isApiKeysPage ? (
                    <ApiKeysPanel
                        apiKeys={apiKeys}
                        onDelete={handleDeleteApiKey}
                        onCreate={handleCreateApiKey}
                        isLoading={apiKeysLoading}
                        newlyCreatedKey={newlyCreatedKey}
                        onDismissNewKey={() => setNewlyCreatedKey(null)}
                    />
                ) : isProfilePage ? (
                    <ProfilePanel user={user} onUserUpdate={setUser} />
                ) : isPlanBillingPage ? (
                    <PlanBillingPanel userPlan={user.plan || 'free'} />
                ) : (
                    <div className="max-w-[1000px] mx-auto p-4 sm:p-6">
                    {/* LIGHT Welcome Hero */}
                    <div
                        className="rounded-xl p-6 mb-6 border border-[#e5e7eb] dark:border-white/15 bg-white dark:bg-[#282929]"
                    >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <h1
                                    className="text-xl font-bold text-[#1a1a1a] dark:text-white mb-1"
                                    style={{ fontFamily: "'Lora', serif" }}
                                >
                                    Welcome back, <span className="text-[#2d8a2d]">{userName}</span>
                                </h1>
                                <p className="text-[#5a5a5a] dark:text-[#e0e0e0] text-sm mb-3">
                                    Your AI MCPs are ready. Let's build something amazing
                                </p>

                                {/* Quick Install Prompt */}
                                <div className="flex items-center gap-2 bg-[#f8fdf7] dark:bg-[rgba(155,227,143,0.10)] border border-[#e8f5e3] dark:border-white/15 rounded-lg px-3 py-2">
                                    <Sparkles className="w-4 h-4 text-[#9be38f] flex-shrink-0" />
                                    <span className="text-[#5a5a5a] dark:text-[#e0e0e0] text-xs">
                                        Simply type <code className="text-[#2d8a2d] dark:text-[#9be38f] bg-[#e8f5e3] dark:bg-[rgba(155,227,143,0.15)] px-1.5 py-0.5 rounded text-xs">"hey pluckr add razorpay payment gateway"</code> in your AI builder
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* MCP Catalog (Landing style) */}
                    <div className="bg-[#f8f9fa] dark:bg-[#161616] rounded-xl border border-[#e5e7eb] dark:border-white/10 p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
                            <div>
                                <h2 className="text-base sm:text-lg font-semibold text-[#1a1a1a] dark:text-white">Plugins</h2>
                                <p className="text-xs sm:text-sm text-[#5a5a5a] dark:text-[#e0e0e0] mt-1">
                                    {unlockedMcps.length > 0
                                        ? `You have ${unlockedMcps.length} MCP${unlockedMcps.length > 1 ? 's' : ''} unlocked.`
                                        : 'Unlock MCPs to get started.'}
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                            {dashboardCards.map((card) => (
                                <DashboardMcpCard
                                    key={card.id}
                                    card={card}
                                    onPluckNow={() => {
                                        navigate('/dashboard/plan-billing');
                                    }}
                                    onQuickInstall={(selectedCard) => {
                                        setSelectedMcp(selectedCard);
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                    </div>
                )}
            </main>

            {/* MCP Connection Modal */}
            <McpConnectionModal
                isOpen={!!selectedMcp}
                onClose={() => setSelectedMcp(null)}
                mcp={selectedMcp}
            />
        </div>
    );
}
